import {
  USER_FORM_VALIDATION,
  ROLE_FORM_VALIDATION,
} from '../constants/userManagement'
import { isValidRole } from './permissions'
import type { UserFormData, RoleFormData, ValidationResult } from '../types'

/**
 * Validates user form data
 */
export function validateUserForm(
  data: Partial<UserFormData>
): ValidationResult {
  const errors: string[] = []

  // Email validation
  if (!data.email) {
    errors.push('El email es requerido')
  } else if (!USER_FORM_VALIDATION.EMAIL.PATTERN.test(data.email)) {
    errors.push('El formato del email no es válido')
  } else if (data.email.length > USER_FORM_VALIDATION.EMAIL.MAX_LENGTH) {
    errors.push(
      `El email no puede tener más de ${USER_FORM_VALIDATION.EMAIL.MAX_LENGTH} caracteres`
    )
  }

  // Name validation
  if (!data.name) {
    errors.push('El nombre es requerido')
  } else if (data.name.length < USER_FORM_VALIDATION.NAME.MIN_LENGTH) {
    errors.push(
      `El nombre debe tener al menos ${USER_FORM_VALIDATION.NAME.MIN_LENGTH} caracteres`
    )
  } else if (data.name.length > USER_FORM_VALIDATION.NAME.MAX_LENGTH) {
    errors.push(
      `El nombre no puede tener más de ${USER_FORM_VALIDATION.NAME.MAX_LENGTH} caracteres`
    )
  } else if (!USER_FORM_VALIDATION.NAME.PATTERN.test(data.name)) {
    errors.push('El nombre solo puede contener letras y espacios')
  }

  // Role validation
  if (!data.role) {
    errors.push('El rol es requerido')
  } else if (!isValidRole(data.role)) {
    errors.push('El rol seleccionado no es válido')
  }

  // Phone validation (optional)
  if (
    data.metadata?.phone &&
    !USER_FORM_VALIDATION.PHONE.PATTERN.test(data.metadata.phone)
  ) {
    errors.push('El formato del teléfono no es válido')
  }

  // Department validation (optional)
  if (
    data.metadata?.department &&
    data.metadata.department.length > USER_FORM_VALIDATION.DEPARTMENT.MAX_LENGTH
  ) {
    errors.push(
      `El departamento no puede tener más de ${USER_FORM_VALIDATION.DEPARTMENT.MAX_LENGTH} caracteres`
    )
  }

  // Position validation (optional)
  if (
    data.metadata?.position &&
    data.metadata.position.length > USER_FORM_VALIDATION.POSITION.MAX_LENGTH
  ) {
    errors.push(
      `El cargo no puede tener más de ${USER_FORM_VALIDATION.POSITION.MAX_LENGTH} caracteres`
    )
  }

  return {
    isValid: errors.length === 0,
    errors,
  }
}

/**
 * Validates role form data
 */
export function validateRoleForm(
  data: Partial<RoleFormData>
): ValidationResult {
  const errors: string[] = []

  // Name validation
  if (!data.name) {
    errors.push('El nombre del rol es requerido')
  } else if (data.name.length < ROLE_FORM_VALIDATION.NAME.MIN_LENGTH) {
    errors.push(
      `El nombre debe tener al menos ${ROLE_FORM_VALIDATION.NAME.MIN_LENGTH} caracteres`
    )
  } else if (data.name.length > ROLE_FORM_VALIDATION.NAME.MAX_LENGTH) {
    errors.push(
      `El nombre no puede tener más de ${ROLE_FORM_VALIDATION.NAME.MAX_LENGTH} caracteres`
    )
  } else if (!ROLE_FORM_VALIDATION.NAME.PATTERN.test(data.name)) {
    errors.push('El nombre solo puede contener letras y espacios')
  }

  // Description validation
  if (!data.description) {
    errors.push('La descripción es requerida')
  } else if (
    data.description.length < ROLE_FORM_VALIDATION.DESCRIPTION.MIN_LENGTH
  ) {
    errors.push(
      `La descripción debe tener al menos ${ROLE_FORM_VALIDATION.DESCRIPTION.MIN_LENGTH} caracteres`
    )
  } else if (
    data.description.length > ROLE_FORM_VALIDATION.DESCRIPTION.MAX_LENGTH
  ) {
    errors.push(
      `La descripción no puede tener más de ${ROLE_FORM_VALIDATION.DESCRIPTION.MAX_LENGTH} caracteres`
    )
  }

  // Permissions validation
  if (!data.permissions) {
    errors.push('Los permisos son requeridos')
  }

  return {
    isValid: errors.length === 0,
    errors,
  }
}

/**
 * Validates email format
 */
export function isValidEmail(email: string): boolean {
  return USER_FORM_VALIDATION.EMAIL.PATTERN.test(email)
}

/**
 * Validates phone format
 */
export function isValidPhone(phone: string): boolean {
  return USER_FORM_VALIDATION.PHONE.PATTERN.test(phone)
}

/**
 * Validates name format
 */
export function isValidName(name: string): boolean {
  const trimmedName = name.trim()
  return (
    trimmedName.length >= USER_FORM_VALIDATION.NAME.MIN_LENGTH &&
    trimmedName.length <= USER_FORM_VALIDATION.NAME.MAX_LENGTH &&
    /^[a-zA-ZÀ-ÿ\u00f1\u00d1\s\-'\.]+$/.test(trimmedName)
  )
}

/**
 * Sanitizes user input to prevent XSS
 */
export function sanitizeInput(input: string): string {
  return input
    .trim()
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers
}

/**
 * Validates password strength (for temporary passwords)
 */
export function validatePasswordStrength(password: string): ValidationResult {
  const errors: string[] = []

  if (password.length < 8) {
    errors.push('La contraseña debe tener al menos 8 caracteres')
  }

  if (!/[A-Z]/.test(password)) {
    errors.push('La contraseña debe contener al menos una letra mayúscula')
  }

  if (!/[a-z]/.test(password)) {
    errors.push('La contraseña debe contener al menos una letra minúscula')
  }

  if (!/\d/.test(password)) {
    errors.push('La contraseña debe contener al menos un número')
  }

  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('La contraseña debe contener al menos un carácter especial')
  }

  return {
    isValid: errors.length === 0,
    errors,
  }
}

/**
 * Generates a random temporary password
 */
export function generateTemporaryPassword(): string {
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const lowercase = 'abcdefghijklmnopqrstuvwxyz'
  const numbers = '0123456789'
  const symbols = '!@#$%^&*'

  const allChars = uppercase + lowercase + numbers + symbols

  let password = ''

  // Ensure at least one character from each category
  password += uppercase[Math.floor(Math.random() * uppercase.length)]
  password += lowercase[Math.floor(Math.random() * lowercase.length)]
  password += numbers[Math.floor(Math.random() * numbers.length)]
  password += symbols[Math.floor(Math.random() * symbols.length)]

  // Fill the rest randomly
  for (let i = 4; i < 12; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)]
  }

  // Shuffle the password
  return password
    .split('')
    .sort(() => Math.random() - 0.5)
    .join('')
}
